import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../context/AuthContext";
import { useAlert } from "../components/AlertModal";
import {
  fetchShiftMonthView,
  bulkUpsertShiftAssignments,
} from "../api/shiftApi";
import Holidays from "date-holidays";
import "../styles/ShiftManagementPage.css";

/* ==============================
  日付関連ユーティリティ
============================== */

const formatMonthValue = (year, month) => {
  return `${year}-${String(month).padStart(2, "0")}`;
};

const getTodayMonthValue = () => {
  const now = new Date();
  return formatMonthValue(now.getFullYear(), now.getMonth() + 1);
};

const parseMonthValue = (value) => {
  const [year, month] = value.split("-").map(Number);
  return { year, month };
};

const getWeekdayLabel = (dateStr) => {
  const d = new Date(dateStr);
  return ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
};

const getDayType = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay();
  if (day === 0) return "sun";
  if (day === 6) return "sat";
  return "weekday";
};

const hd = new Holidays("JP");

const isHoliday = (dateStr) => {
  return !!hd.isHoliday(new Date(dateStr));
};
/* ==============================
  月移動処理
============================== */
const shiftMonth = (value, diff) => {
  const [year, month] = value.split("-").map(Number);

  const d = new Date(year, month - 1 + diff);

  const newYear = d.getFullYear();
  const newMonth = d.getMonth() + 1;

  return `${newYear}-${String(newMonth).padStart(2, "0")}`;
};
/* ==============================
  時間フォーマット
============================== */

const formatHourOnly = (time) => {
  if (!time) return "";
  return String(time).slice(0, 2).replace(/^0/, "");
};

const formatTimeRange = (startTime, endTime) => {
  if (startTime && endTime) {
    return `${formatHourOnly(startTime)}-${formatHourOnly(endTime)}`;
  }
  if (startTime) {
    return `${formatHourOnly(startTime)}-`;
  }
  if (endTime) {
    return `-${formatHourOnly(endTime)}`;
  }
  return "";
};

/* ==============================
  ★ 週分割ユーティリティ
============================== */

const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

function ShiftManagementPage() {
  const navigate = useNavigate();
  const { user: userInfo, loading } = useAuthContext();
  const { showAlert } = useAlert();

  const [monthValue, setMonthValue] = useState(getTodayMonthValue());
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);

  const [monthData, setMonthData] = useState(null);
  const [editedCells, setEditedCells] = useState({});
  const [editorCell, setEditorCell] = useState(null);
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  const { year, month } = useMemo(() => parseMonthValue(monthValue), [monthValue]);

  const [shareTypeModalOpen, setShareTypeModalOpen] = useState(false);

  // 編集できるユーザーかどうか
  const canEditShift = !!userInfo?.is_staff;

  /* ==============================
    データ取得
  ============================== */
  const loadData = useCallback(async () => {
    if (!userInfo?.store_id) return;

    setLoadingPage(true);
    try {
      const data = await fetchShiftMonthView({
        storeId: userInfo.store_id,
        year,
        month,
      });
      setMonthData(data);
      setEditedCells({});
      setEditorCell(null);
    } catch (error) {
      console.error("shift month_view error:", error);
      showAlert("シフトデータの取得に失敗しました。");
    } finally {
      setLoadingPage(false);
    }
  }, [userInfo?.store_id, year, month, showAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ==============================
    編集処理
  ============================== */
  const handleCellChange = (staffId, targetDate, key, value) => {
    const cellKey = `${staffId}_${targetDate}`;
    const row = monthData?.rows?.find((r) => r.staff_id === staffId);
    const originalCell =
      row?.cells?.find((cell) => cell.target_date === targetDate) || null;

    setEditedCells((prev) => {
      const base = prev[cellKey] || {
        staff: staffId,
        staff_name: row?.staff_name || "",
        store: userInfo.store_id,
        target_date: targetDate,
        assignment_id: originalCell?.assignment_id || null,
        is_off: originalCell?.is_off || false,
        is_comp_off: originalCell?.is_comp_off || false,
        location: originalCell?.location || null,
        location_name: originalCell?.location_name || "",
        location_short_name: originalCell?.location_short_name || "",
        location_color: originalCell?.location_color || "",
        start_time: originalCell?.start_time || "",
        end_time: originalCell?.end_time || "",
        memo: originalCell?.memo || "",
        is_locked: originalCell?.is_locked || false,
        has_off_request: originalCell?.has_off_request || false,
      };

      const next = {
        ...base,
        [key]: value,
      };

      if (key === "location") {
        const selectedLocation = (monthData?.locations || []).find(
          (loc) => loc.id === value
        );

        next.location_name = selectedLocation ? selectedLocation.name : "";
        next.location_short_name = selectedLocation
          ? (selectedLocation.short_name || "")
          : "";
        next.location_color = selectedLocation
          ? (selectedLocation.color || "")
          : "";
      }

      if (key === "is_off" && value === true) {
        next.is_comp_off = false;

        next.location = null;
        next.location_name = "";
        next.location_short_name = "";
        next.location_color = "";
        next.start_time = "";
        next.end_time = "";
      }

      if (key === "is_comp_off" && value === true) {
        next.is_off = false;

        next.location = null;
        next.location_name = "";
        next.location_short_name = "";
        next.location_color = "";
        next.start_time = "";
        next.end_time = "";
      }

      return {
        ...prev,
        [cellKey]: next,
      };
    });
  };

  const getDisplayCell = (staffId, targetDate) => {
    const cellKey = `${staffId}_${targetDate}`;
    const edited = editedCells[cellKey];

    if (edited) return edited;

    const row = monthData?.rows?.find((r) => r.staff_id === staffId);
    return row?.cells?.find((cell) => cell.target_date === targetDate) || null;
  };

  const openEditor = (staffId, targetDate) => {
    // 閲覧専用ユーザーはモーダルを開かせない
    if (!canEditShift) return;

    const displayCell = getDisplayCell(staffId, targetDate);
    const row = monthData?.rows?.find((r) => r.staff_id === staffId);

    if (!displayCell || !row) return;

    setEditorCell({
      staffId,
      targetDate,
      staffName: row.staff_name,
    });
  };

  const closeEditor = () => {
    setEditorCell(null);
  };

  const currentEditorData = useMemo(() => {
    if (!editorCell) return null;
    return getDisplayCell(editorCell.staffId, editorCell.targetDate);
  }, [editorCell, editedCells, monthData]);

  const buildSavePayload = () => {
    return Object.values(editedCells).map((item) => {
      const isDayOff = item.is_off || item.is_comp_off;

      return {
        store: item.store,
        staff: item.staff,
        target_date: item.target_date,

        location: isDayOff ? null : item.location || null,
        start_time: isDayOff ? null : item.start_time || null,
        end_time: isDayOff ? null : item.end_time || null,

        is_off: !!item.is_off,
        is_comp_off: !!item.is_comp_off,

        memo: item.memo || "",
        is_locked: !!item.is_locked,
      };
    });
  };

  const handleSave = async () => {
    const payload = buildSavePayload();

    if (payload.length === 0) {
      showAlert("変更はありません。");
      return;
    }

    setSaving(true);
    try {
      await bulkUpsertShiftAssignments(payload);
      showAlert("保存しました。");
      await loadData();
    } catch (error) {
      console.error("bulk_upsert error:", error);
      const detail =
        error?.details?.error ||
        error?.details?.detail ||
        "保存に失敗しました。";
      showAlert(detail);
    } finally {
      setSaving(false);
    }
  };

  /* ==============================
    ★ 週2休未満判定
  ============================== */
  const hasLessThanTwoOffsInWeek = (staffId, weekDates) => {
    let offCount = 0;

    for (const d of weekDates) {
      const cell = getDisplayCell(staffId, d);
      if (cell?.is_off) offCount++;
    }

    return offCount < 2;
  };

  /* ==============================
    時間指定あり判定
    - 時間未入力 = 終日勤務なので false
    - start_time or end_time が入っていれば true
  ============================== */
  const hasCustomTime = (cell) => {
    if (!cell || cell.is_off) return false;
    return !!(cell.start_time || cell.end_time);
  };

  /* ==============================
    表示まとめ
  ============================== */
  const getCellSummary = (cell) => {
    if (!cell) return { main: "", sub: "", className: "", color: "" };

    if (cell.is_comp_off) {
      return {
        main: "休",
        sub: "(仮)",
        className: "is-comp-off",
        color: "",
      };
    }

    if (cell.is_off) {
      return {
        main: "休",
        sub: cell.has_off_request ? "希望" : "",
        className: "is-off",
        color: "",
      };
    }

    const locationText =
      cell.location_short_name ||
      cell.default_location_short_name ||
      cell.location_name ||
      cell.default_location_name ||
      "";

    const timeText = formatTimeRange(cell.start_time, cell.end_time);

    return {
      main: locationText || "未",
      sub: timeText || "",
      className: cell.has_off_request ? "has-off-request" : "",
      color: cell.location_color || "",
    };
  };

  const locations = monthData?.locations || [];
  const rows = monthData?.rows || [];
  const dates = monthData?.dates || [];

  /* ==============================
    ★ 表示用に日付を7日ずつで週分割
  ============================== */
  const weekChunks = useMemo(() => {
    return chunkArray(dates, 7);
  }, [dates]);

  /* ==============================
    フッター表示用
    各日付ごとの勤務場所人数を集計
  ============================== */
  const locationCountByDate = useMemo(() => {
    const result = {};

    dates.forEach((dateStr) => {
      result[dateStr] = {};

      rows.forEach((row) => {
        const cell = getDisplayCell(row.staff_id, dateStr);

        // 休みは人数に含めない
        if (!cell || cell.is_off) return;

        // 編集後 location を優先、なければ元データ側も見る
        const locationId = cell.location ?? cell.default_location ?? null;

        if (!locationId) return;

        if (!result[dateStr][locationId]) {
          result[dateStr][locationId] = 0;
        }

        result[dateStr][locationId] += 1;
      });
    });

    return result;
  }, [dates, rows, editedCells, monthData]);

  /* ==============================
    休み集計
  ============================== */
  const getStaffOffTotal = (staffId) => {
    let count = 0;

    dates.forEach((dateStr) => {
      const cell = getDisplayCell(staffId, dateStr);
      if (cell?.is_off) count++;
    });

    return count;
  };

  /* ==============================
    選択スタッフのシフト共有
  ============================== */
  const formatShareTime = (time) => {
    if (!time) return "";
    return String(time).slice(0, 5);
  };

  const handleShareStaffShift = async (type) => {
  if (!selectedStaffId) {
    showAlert("共有するスタッフを選択してください。");
    return;
  }

  const row = rows.find((r) => r.staff_id === selectedStaffId);

  if (!row) {
    showAlert("スタッフ情報が見つかりません。");
    return;
  }

  const isWork = type === "work";
  const typeLabel = isWork ? "出勤日" : "休日";

  const shiftLines = dates
    .map((dateStr) => {
      const cell = getDisplayCell(row.staff_id, dateStr);

      if (!cell) return null;

      // 出勤日を選択 → 休日は除外
      if (isWork && cell.is_off) {
        return null;
      }

      // 休日を選択 → 出勤日は除外
      if (!isWork && !cell.is_off) {
        return null;
      }

      const d = new Date(`${dateStr}T00:00:00`);
      const month = d.getMonth() + 1;
      const day = d.getDate();
      const weekday = getWeekdayLabel(dateStr);

      // 休日の場合は日付だけ
      if (!isWork) {
        return `${month}/${day}(${weekday})`;
      }

      // 出勤日の場合は時間も表示
      const startTime = cell.start_time
        ? String(cell.start_time).slice(0, 5)
        : "";

      const endTime = cell.end_time
        ? String(cell.end_time).slice(0, 5)
        : "";

      let timeText = "11:00~20:00";

      if (startTime && endTime) {
        timeText = `${startTime}～${endTime}`;
      } else if (startTime) {
        timeText = `${startTime}～`;
      } else if (endTime) {
        timeText = `～${endTime}`;
      }

      return `${month}/${day}(${weekday}) ${timeText}`;
    })
    .filter(Boolean);

  if (shiftLines.length === 0) {
    showAlert(`${typeLabel}がありません。`);
    return;
  }

  const shareText = [
    `${row.staff_name}(${typeLabel})`,
    "",
    shiftLines.join("\n"),
  ].join("\n");

  setShareTypeModalOpen(false);

  try {
    if (navigator.share) {
      await navigator.share({
        title: `${row.staff_name}(${typeLabel})`,
        text: shareText,
      });
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareText);
      showAlert("シフトをコピーしました。");
      return;
    }

    throw new Error("Clipboard API unavailable");

  } catch (error) {
    if (error?.name === "AbortError") {
      return;
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = shareText;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";

      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      const copied = document.execCommand("copy");

      document.body.removeChild(textarea);

      if (copied) {
        showAlert("シフトをコピーしました。");
      } else {
        showAlert("コピーに失敗しました。");
      }
    } catch (copyError) {
      console.error("シフトコピーエラー:", copyError);
      showAlert("コピーに失敗しました。");
    }
  }
};
  
  /* ==============================
    前月翌月ボタンハンドラ
  ============================== */
  const handlePrevMonth = () => {
    setMonthValue((prev) => shiftMonth(prev, -1));
  };

  const handleNextMonth = () => {
    setMonthValue((prev) => shiftMonth(prev, 1));
  };

  if (loading || !userInfo) return <div>読み込み中...</div>;
    /* ==============================
    今日判定を追加
  ============================== */
  const todayStr = new Date().toLocaleDateString("sv-SE", {
    timeZone: "Asia/Tokyo",
  });


  return (
    <div className="shift-page">
      <div className="shift-page-header">
        <div className="shift-page-title-area">
          <h2 className="shift-page-title">シフト管理</h2>
          <div className="shift-page-store">店舗ID: {userInfo.store_id}</div>
        </div>

        <div className="shift-page-controls">
          <div className="shift-month-control">
            <button
              type="button"
              className="shift-month-btn"
              onClick={handlePrevMonth}
            >
              ←
            </button>

            <input
              type="month"
              value={monthValue}
              onChange={(e) => setMonthValue(e.target.value)}
              className="shift-month-input"
            />

            <button
              type="button"
              className="shift-month-btn"
              onClick={handleNextMonth}
            >
              →
            </button>
          </div>

          <button
            type="button"
            className="shift-btn"
            onClick={loadData}
            disabled={loadingPage}
          >
            {loadingPage ? "読込中..." : "再読込"}
          </button>
          {canEditShift && selectedStaffId && (
            <button
              type="button"
              className="shift-btn"
              onClick={() => setShareTypeModalOpen(true)}
            >
              共有
            </button>
          )}

          {canEditShift && (
            <button
              type="button"
              className="shift-btn"
              onClick={() => navigate("/shift_request_off")}
            >
              希望休入力
            </button>
          )}

          {canEditShift && (
            <>
              <button
                type="button"
                className="shift-btn"
                onClick={() => navigate("/shift_staff_master")}
              >
                スタッフマスター
              </button>

              <button
                type="button"
                className="shift-btn"
                onClick={() => navigate("/shift_location_master")}
              >
                勤務場所マスター
              </button>
            </>
          )}

          {canEditShift && (
            <button
              type="button"
              className="shift-btn shift-btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "保存中..." : "保存"}
            </button>
          )}
        </div>
      </div>

      <div className="shift-page-note">
        {canEditShift
          ? "セルは圧縮表示です。クリックすると編集できます。"
          : "閲覧専用です。シフトの確認のみ可能です。"}
      </div>

      {loadingPage ? (
        <div className="shift-loading">シフトを読み込み中...</div>
      ) : (
        <div className="shift-table-wrap">
          <table className="shift-table">
            <thead>
              <tr>
                <th className="shift-staff-col">スタッフ</th>
                {dates.map((dateStr) => {
                  const day = Number(dateStr.slice(-2));
                  const weekday = getWeekdayLabel(dateStr);
                  const isSun = getDayType(dateStr) === "sun";
                  const isSat = getDayType(dateStr) === "sat";
                  const holiday = isHoliday(dateStr);
                  const isToday = dateStr === todayStr;

                  return (
                    <th
                      key={dateStr}
                      className={`shift-date-col
                        ${isSat ? "is-sat" : ""}
                        ${isSun || holiday ? "is-sun" : ""}
                        ${isToday ? "is-today-col" : ""}
                      `}
                    >
                      <div>{day}</div>
                      <div
                        className={`shift-weekday
                          ${isSat ? "is-sat" : ""}
                          ${isSun || holiday ? "is-sun" : ""}
                        `}
                      >
                        {weekday}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.staff_id}
                  className={
                    selectedStaffId &&
                    selectedStaffId !== row.staff_id
                      ? "is-dimmed-row"
                      : ""
                  }
                >
                  <td
                    className={`shift-staff-cell ${
                      selectedStaffId === row.staff_id ? "is-selected-staff" : ""
                    }`}
                    onClick={() =>
                      setSelectedStaffId((prev) =>
                        prev === row.staff_id ? null : row.staff_id
                      )
                    }
                  >
                    {row.staff_name}({getStaffOffTotal(row.staff_id)})
                  </td>

                  {row.cells.map((cell, colIndex) => {
                    const displayCell = getDisplayCell(row.staff_id, cell.target_date);
                    const summary = getCellSummary(displayCell);

                    const weekIndex = Math.floor(colIndex / 7);
                    const weekDates = weekChunks[weekIndex] || [];

                    const isLessThanTwoOffs = hasLessThanTwoOffsInWeek(
                      row.staff_id,
                      weekDates
                    );

                    const isWeekStart = weekDates[0] === cell.target_date;
                    const isWeekEnd =
                      weekDates[weekDates.length - 1] === cell.target_date;

                    return (
                      <td
                        key={`${row.staff_id}_${cell.target_date}`}
                        className={`
                          shift-cell
                          ${summary.className}
                          ${displayCell?.is_readonly ? "is-readonly" : ""}
                          ${isLessThanTwoOffs ? "week-warning-cell" : ""}
                          ${isLessThanTwoOffs && isWeekStart ? "week-warning-start" : ""}
                          ${isLessThanTwoOffs && isWeekEnd ? "week-warning-end" : ""}
                          ${hasCustomTime(displayCell) ? "has-custom-time" : ""}
                          ${cell.target_date === todayStr ? "is-today-col" : ""}
                          ${selectedStaffId === row.staff_id ? "is-selected-staff-row" : ""}
                        `}
                        style={{
                          backgroundColor: summary.color || undefined,
                          color: summary.color ? "#666666" : undefined,
                          cursor:
                            canEditShift && !displayCell?.is_readonly
                              ? "pointer"
                              : "default",
                        }}
                        onClick={() => {
                          if (canEditShift && !displayCell?.is_readonly) {
                            openEditor(row.staff_id, cell.target_date);
                          }
                        }}
                      >
                        <div className="shift-cell-mini">
                          <div className="shift-cell-main">{summary.main}</div>
                          <div className="shift-cell-sub">{summary.sub}</div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            <tfoot>
              {locations.map((location) => (
                <tr key={`footer_location_${location.id}`}>
                  <td className="shift-footer-label">
                    {location.short_name || location.name}
                  </td>

                  {dates.map((dateStr) => {
                    const count = locationCountByDate?.[dateStr]?.[location.id] || 0;

                    return (
                      <td
                        key={`footer_${location.id}_${dateStr}`}
                        className={`shift-footer-cell ${
                          count > 0 && count < 2 ? "is-few" : ""
                        }`}
                      >
                        {count > 0 ? `${count}` : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      )}
      {shareTypeModalOpen && (
        <div
          className="shift-editor-overlay"
          onClick={() => setShareTypeModalOpen(false)}
        >
          <div
            className="shift-editor-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shift-editor-header">
              <div className="shift-editor-title">
                共有する内容を選択
              </div>

              <button
                type="button"
                className="shift-editor-close"
                onClick={() => setShareTypeModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="shift-editor-body">
              <button
                type="button"
                className="shift-btn"
                onClick={() => handleShareStaffShift("work")}
              >
                出勤日
              </button>

              <button
                type="button"
                className="shift-btn"
                onClick={() => handleShareStaffShift("off")}
              >
                休日
              </button>
            </div>
          </div>
        </div>
      )}
      {canEditShift && editorCell && currentEditorData && (
        <div className="shift-editor-overlay" onClick={closeEditor}>
          <div
            className="shift-editor-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shift-editor-header">
              <div>
                <div className="shift-editor-title">{editorCell.staffName}</div>
                <div className="shift-editor-date">{editorCell.targetDate}</div>
              </div>

              <button
                type="button"
                className="shift-editor-close"
                onClick={closeEditor}
              >
                ×
              </button>
            </div>

            <div className="shift-editor-body">
              <label className="shift-editor-check">
                <input
                  type="checkbox"
                  checked={!!currentEditorData.is_off}
                  onChange={(e) =>
                    handleCellChange(
                      editorCell.staffId,
                      editorCell.targetDate,
                      "is_off",
                      e.target.checked
                    )
                  }
                />
                <span>休みにする</span>
              </label>
              <label className="shift-editor-check">
                <input
                  type="checkbox"
                  checked={!!currentEditorData.is_comp_off}
                  onChange={(e) =>
                    handleCellChange(
                      editorCell.staffId,
                      editorCell.targetDate,
                      "is_comp_off",
                      e.target.checked
                    )
                  }
                />
                <span>代休にする</span>
              </label>

              {currentEditorData.has_off_request && (
                <div className="shift-editor-request-note">
                  この日は希望休が入っています
                </div>
              )}

              {!currentEditorData.is_off && !currentEditorData.is_comp_off && (
                <>
                  <div className="shift-editor-row">
                    <label className="shift-editor-label">勤務場所</label>
                    <select
                      className="shift-editor-select"
                      value={currentEditorData.location ?? ""}
                      onChange={(e) =>
                        handleCellChange(
                          editorCell.staffId,
                          editorCell.targetDate,
                          "location",
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    >
                      <option value="">場所を選択</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.short_name || location.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="shift-editor-time-row">
                    <div className="shift-editor-time-box">
                      <label className="shift-editor-label">開始</label>
                      <input
                        type="time"
                        className="shift-editor-time"
                        value={currentEditorData.start_time || ""}
                        onChange={(e) =>
                          handleCellChange(
                            editorCell.staffId,
                            editorCell.targetDate,
                            "start_time",
                            e.target.value
                          )
                        }
                      />
                    </div>

                    <div className="shift-editor-time-box">
                      <label className="shift-editor-label">終了</label>
                      <input
                        type="time"
                        className="shift-editor-time"
                        value={currentEditorData.end_time || ""}
                        onChange={(e) =>
                          handleCellChange(
                            editorCell.staffId,
                            editorCell.targetDate,
                            "end_time",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="shift-editor-row">
                <label className="shift-editor-label">メモ</label>
                <textarea
                  className="shift-editor-memo"
                  value={currentEditorData.memo || ""}
                  onChange={(e) =>
                    handleCellChange(
                      editorCell.staffId,
                      editorCell.targetDate,
                      "memo",
                      e.target.value
                    )
                  }
                  rows={4}
                />
              </div>
            </div>

            <div className="shift-editor-footer">
              <button
                type="button"
                className="shift-btn"
                onClick={closeEditor}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShiftManagementPage;